import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { CR_API_ENDPOINTS } from '../../../constants';
import { apiService } from '../../../services/api.service';

interface ImportResult {
  inserted_count: number;
  updated_count: number;
  skipped_count: number;
}

interface ClientCatalogImportModalProps {
  isOpen: boolean;
  clientId: string;
  clientName: string;
  onClose: () => void;
}

export const ClientCatalogImportModal: React.FC<ClientCatalogImportModalProps> = ({
  isOpen,
  clientId,
  clientName,
  onClose,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSelectedFile(event.target.files?.[0] ?? null);
    setResult(null);
  };

  const handleUpload = async (): Promise<void> => {
    if (!selectedFile) return;

    setIsUploading(true);
    setResult(null);

    try {
      const importResult = await apiService.uploadFile<ImportResult>(
        `${CR_API_ENDPOINTS.NETSUITE_ITEMS}/import/${clientId}`,
        selectedFile,
      );
      setResult(importResult);
      toast.success(
        `${importResult.inserted_count} inserted, ${importResult.updated_count} updated` +
          (importResult.skipped_count > 0 ? ` (${importResult.skipped_count} duplicates skipped)` : ''),
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = (): void => {
    setSelectedFile(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md mx-4">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Import Client Catalog</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Upload a CSV file for <span className="font-medium text-slate-700">{clientName}</span>.
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">CSV File (.csv)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isUploading}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800
                         file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1
                         file:text-xs file:font-medium file:text-slate-700 disabled:opacity-50"
            />
          </div>

          <button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            className="w-full rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white
                       shadow-sm transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isUploading ? 'Uploading...' : 'Upload & Import'}
          </button>

          {result && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800 space-y-1">
              <div>
                <span className="font-semibold text-emerald-700">{result.inserted_count}</span>
                <span> new items inserted</span>
              </div>
              <div>
                <span className="font-semibold text-slate-700">{result.updated_count}</span>
                <span className="text-slate-600"> existing items updated</span>
              </div>
              {result.skipped_count > 0 && (
                <div>
                  <span className="font-semibold text-amber-600">{result.skipped_count}</span>
                  <span className="text-slate-600"> duplicates skipped</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end border-t border-slate-200 px-6 py-4">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            {result ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};
